import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, AlertCircle, Info, BarChart3 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface IssuesSummaryData {
  high: number;
  medium: number;
  low: number;
  total: number;
}

interface IssuesSummaryCardProps {
  data: IssuesSummaryData | null;
  isLoading?: boolean;
}

export const IssuesSummaryCard: React.FC<IssuesSummaryCardProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Issues Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const total = data?.total ?? 0;
  const high = data?.high ?? 0;
  const medium = data?.medium ?? 0;
  const low = data?.low ?? 0;

  const highPercent = total > 0 ? Math.round((high / total) * 100) : 0;
  const mediumPercent = total > 0 ? Math.round((medium / total) * 100) : 0;
  const lowPercent = total > 0 ? Math.round((low / total) * 100) : 0;

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="text-foreground flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Issues Overview
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-foreground">{total.toLocaleString()}</div>
            <div className="text-sm text-muted-foreground mt-1">Total Issues</div>
          </div>
          <div className="bg-destructive/10 rounded-lg p-4 text-center border border-destructive/20">
            <div className="flex items-center justify-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <span className="text-3xl font-bold text-destructive">{high.toLocaleString()}</span>
            </div>
            <div className="text-sm text-muted-foreground mt-1">High Impact ({highPercent}%)</div>
          </div>
          <div className="bg-orange-500/10 rounded-lg p-4 text-center border border-orange-500/20">
            <div className="flex items-center justify-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              <span className="text-3xl font-bold text-orange-500">{medium.toLocaleString()}</span>
            </div>
            <div className="text-sm text-muted-foreground mt-1">Medium Impact ({mediumPercent}%)</div>
          </div>
          <div className="bg-green-500/10 rounded-lg p-4 text-center border border-green-500/20">
            <div className="flex items-center justify-center gap-2">
              <Info className="h-5 w-5 text-green-500" />
              <span className="text-3xl font-bold text-green-500">{low.toLocaleString()}</span>
            </div>
            <div className="text-sm text-muted-foreground mt-1">Low Impact ({lowPercent}%)</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
